import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography } from 'antd';
import MainLayout from '../../components/MainLayout';
import StatusMessages, { StatusMessage } from '../../components/StatusMessages';
import ResetPasswordForm, { ResetPasswordInfo } from '../../components/ResetPasswordForm';
import { userResetPassword } from '../../api/accounts';
import { LOGIN_ROUTE } from '../../config/constants';
import styles from './ResetPassword.module.css';

export interface ResetPasswordProps {};

// takes in reset password token and resets user password
const ResetPassword = (props: ResetPasswordProps): JSX.Element | null => {
  // list of status messages
  const [statusMessages, setStatusMessages] = useState<StatusMessage[]>([]);

  const navigate = useNavigate();
  // get token from URL parameters
  const { token } = useParams();

  // set document title
  useEffect(() => {
    document.title = 'Reset Password | Sigmoid Academy';
  }, []);

  useEffect(() => {
    // redirect to login page if token not found in URL parameteres
    if (token == null) {
      navigate(LOGIN_ROUTE);
      return;
    }
  }, [navigate, token]);

  const handleResetPassword = async (info: ResetPasswordInfo): Promise<void> => {
    // redirect to login page if token not found in URL parameteres
    if (token == null) {
      navigate(LOGIN_ROUTE);
      return;
    }

    // reset user password
    userResetPassword(
      token,
      info.newPassword
    // navigate to login route with success status messages on success
    ).then(() => {
      navigate(
        LOGIN_ROUTE,
        {
          state: {
            statusMessages: [
              {
                message: 'Your password has been reset',
                status: 'success'
              },
            ]
          }
        }
      );
    // display danger status messages on failure
    }).catch((error: AxiosError) => {
      if (error.response?.status !== 400) {
        setStatusMessages(
          (previousMessages: StatusMessage[]) => [{
            message: (error.response?.data as any).detail,
            status: 'error'
          }]
        );

        return;
      }

      // collect list of error messages from response
      let errorStatusMessages: StatusMessage[] = [];
      for (let field in (error.response?.data as any).errors) {
        let messages = (error.response?.data as any).errors[field];

        for (let i = 0; i < messages.length; i++) {
          errorStatusMessages.push({
            message: messages[i].charAt(0).toUpperCase() + messages[i].slice(1),
            status: 'error',
          });
        }
      }

      // set list of errors as status messages
      setStatusMessages((previousMessages: StatusMessage[]) => [...errorStatusMessages]);
    });
  };

  return (
    <div>
      <MainLayout>
        <div className={styles.mainContainer}>
          <Typography.Title
            level={1}
            className={styles.mainHeading}
          >
            Reset Password
          </Typography.Title>
          <StatusMessages items={statusMessages} />
          <ResetPasswordForm handleResetPassword={handleResetPassword} />
        </div>
      </MainLayout>
    </div>
  );
};

export default ResetPassword;